import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SiteLayout, FLAME, FLAME_2, INK, BODY, MUTED, BORDER, MONO } from './SiteLayout';

const SUPPORT_EMAIL = 'Support@ignitorads.com';
const WEB3FORMS_ACCESS_KEY = 'ea674892-d8c8-42e7-9b4c-bf307db5f3b3';

const FREE_FEATURES = [
  'One tool free forever — the Ads.txt Checker',
  'Results in seconds, no card required',
  'Docs & customer support',
];

const SUITE_FEATURES = [
  'All 11+ tools unlocked, or just the ones you need',
  'Approved-list & bundle alerts',
  'Revenue discrepancy monitoring',
  'Fetch Engine for all your partner performance data',
  'Priority support',
  'One-time setup, ongoing value',
];

const inputStyle = {
  width: '100%', height: '46px', padding: '0 14px', fontSize: '14px',
  color: INK, background: '#fbfaf7', border: '1px solid #ddd9d0',
  borderRadius: '10px', fontFamily: "'Space Grotesk', sans-serif",
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', fontSize: '15px', color: BODY, marginBottom: '7px', fontWeight: 600 };

export default function ContactPage() {
  const { hash } = useLocation();
  const [form, setForm] = useState({ name: '', email: '', company: '', teamSize: '1–5 people', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [botcheck, setBotcheck] = useState('');

  // Support /contact#contact deep-links from the nav and footer.
  useEffect(() => {
    if (hash === '#contact') {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  function update(field, val) { setForm(prev => ({ ...prev, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;
    if (botcheck) { setSubmitted(true); return; } // honeypot — silently drop bots
    setLoading(true); setError(null);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `AdTech Hub enquiry — ${form.company}`,
          from_name: form.name,
          email: form.email,
          Company: form.company,
          Team_size: form.teamSize,
          Message: form.message || '(no message provided)',
          botcheck,
        }),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setError(data.message || `Couldn't send your message — please email us at ${SUPPORT_EMAIL}.`);
    } catch (err) {
      setError(`Network error — please email us directly at ${SUPPORT_EMAIL}.`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <SiteLayout>

      {/* ── HERO ── */}
      <section style={{ maxWidth: '1180px', margin: '0 auto', padding: '88px 32px 48px', textAlign: 'center' }}>
        <p style={{ fontFamily: MONO, fontSize: '20px', letterSpacing: '1.6px', color: FLAME, textTransform: 'uppercase', margin: 0 }}>
          Pricing &amp; support
        </p>
        <h1 className="ia-hero-h1" style={{
          fontSize: '56px', lineHeight: 1.04, fontWeight: 600, letterSpacing: '-1.6px',
          margin: '22px auto 0', maxWidth: '760px', textWrap: 'balance',
        }}>
          One free tool. One simple <span style={{ fontStyle: 'italic', color: FLAME }}>subscription</span>.
        </h1>
        <p style={{ fontSize: '18px', color: BODY, maxWidth: '580px', margin: '22px auto 0', lineHeight: 1.55 }}>
          No trials, no per-seat math, no surprises. Start free, then unlock the full suite under a single monthly plan —
          priority support included.
        </p>
      </section>

      {/* ── PRICING ── */}
      <section style={{ maxWidth: '980px', margin: '0 auto', padding: '0 32px 20px' }}>
        <h2 style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          Plans
        </h2>

        <div className="ia-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: '22px', alignItems: 'stretch' }}>

          {/* FREE */}
          <div style={{ background: 'rgba(255,255,255,.9)', border: `1px solid ${BORDER}`, borderRadius: '18px', padding: '34px' }}>
            <div style={{ fontFamily: MONO, fontSize: '14px', letterSpacing: '1px', color: '#16a34a' }}>FREE</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '14px' }}>
              <span style={{ fontSize: '48px', fontWeight: 600, letterSpacing: '-1.5px' }}>$0</span>
              <span style={{ fontSize: '15px', color: MUTED, fontWeight: 700 }}>forever, for 1 tool</span>
            </div>
            <p style={{ fontSize: '16px', color: BODY, marginTop: '6px' }}>For getting a feel of the hub.</p>
            <div style={{ height: '1px', background: '#ece8df', margin: '22px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {FREE_FEATURES.map(f => (
                <div key={f} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: FLAME, fontWeight: 700 }}>✓</span>
                  <span style={{ fontSize: '16px', color: '#3a352d' }}>{f}</span>
                </div>
              ))}
            </div>
            <a href="#contact" style={{
              display: 'block', textAlign: 'center', marginTop: '28px',
              border: `1px solid ${INK}`, color: INK, fontSize: '17px', fontWeight: 500,
              padding: '14px', borderRadius: '28px', textDecoration: 'none',
            }}>Start free</a>
          </div>

          {/* FULL SUITE */}
          <div style={{
            background: INK, color: '#f6f5f2', border: `1px solid ${INK}`,
            borderRadius: '18px', padding: '34px', position: 'relative',
            boxShadow: '0 24px 50px -24px rgba(40,30,18,.5)',
          }}>
            <span style={{
              position: 'absolute', top: '24px', right: '24px', fontFamily: MONO,
              fontSize: '12px', letterSpacing: '1px', color: INK, background: FLAME,
              padding: '5px 12px', borderRadius: '20px', fontWeight: 700,
            }}>PREFERRED BY MOST TEAMS</span>

            <div style={{ fontFamily: MONO, fontSize: '14px', letterSpacing: '1px', color: FLAME_2 }}>FULL SUITE</div>
            <div style={{ fontSize: '48px', fontWeight: 600, letterSpacing: '-1.5px', marginTop: '14px' }}>Let's talk</div>
            <p style={{ fontSize: '16px', color: '#b8b2a7', marginTop: '6px', lineHeight: 1.5 }}>
              One subscription · One login · One bill<br />
              Flexibility to choose the tools you need
            </p>
            <div style={{ height: '1px', background: INK === '#15130f' ? '#2a2620' : '#2a2620', margin: '22px 0' }} />
            <div className="ia-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {SUITE_FEATURES.map(f => (
                <div key={f} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start' }}>
                  <span style={{ color: FLAME_2, fontWeight: 700 }}>✓</span>
                  <span style={{ fontSize: '15px', color: '#dcd6cb' }}>{f}</span>
                </div>
              ))}
            </div>
            <a href="#contact" style={{
              display: 'block', textAlign: 'center', marginTop: '28px',
              background: FLAME, color: INK, fontSize: '17px', fontWeight: 600,
              padding: '15px', borderRadius: '28px', textDecoration: 'none',
            }}>Contact us</a>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontFamily: MONO, fontSize: '15px', color: '#0D0D0D', marginTop: '20px', fontWeight: 700 }}>
          Pricing scales with your team — tell us your size below and we'll send a number.
        </p>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" style={{ maxWidth: '1080px', margin: '0 auto', padding: '72px 32px 60px', scrollMarginTop: '90px' }}>
        <div className="ia-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: '48px', alignItems: 'start' }}>

          {/* Left */}
          <div style={{ paddingTop: '8px' }}>
            <p style={{ fontFamily: MONO, fontSize: '18px', letterSpacing: '1.6px', color: FLAME, textTransform: 'uppercase', margin: 0 }}>
              Talk to us
            </p>
            <h2 style={{ fontSize: '34px', fontWeight: 600, letterSpacing: '-1px', marginTop: '14px', lineHeight: 1.1 }}>
              Tell us about your team.
            </h2>
            <p style={{ fontSize: '18px', color: BODY, lineHeight: 1.6, marginTop: '14px', maxWidth: '380px' }}>
              Whether you want the free tool set up, or a quote for the full suite or just a few tools — drop us a line.
              A real person on the operations side gets back fast.
            </p>

            <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <div style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '1px', color: MUTED, fontWeight: 600 }}>EMAIL</div>
                <a href={`mailto:${SUPPORT_EMAIL}`} style={{ fontSize: '15px', color: INK, marginTop: '4px', display: 'block', textDecoration: 'none' }}>
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <div>
                <div style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '1px', color: MUTED, fontWeight: 600 }}>RESPONSE TIME</div>
                <div style={{ fontSize: '15px', color: INK, marginTop: '4px' }}>Usually within the same business day</div>
              </div>
              <div>
                <div style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '1px', color: MUTED, fontWeight: 600 }}>SUPPORT</div>
                <div style={{ fontSize: '15px', color: INK, marginTop: '4px' }}>Priority support for all subscribers</div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div style={{
            background: 'rgba(255,255,255,.9)', border: `1px solid ${BORDER}`,
            borderRadius: '18px', padding: '32px', backdropFilter: 'blur(8px)',
            boxShadow: '0 20px 44px -28px rgba(40,30,18,.34)',
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', background: '#eefaf0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px', fontSize: '26px', color: '#16a34a',
                }}>✓</div>
                <h3 style={{ fontSize: '22px', fontWeight: 600, margin: '0 0 8px' }}>Message sent</h3>
                <p style={{ color: BODY, fontSize: '15px', lineHeight: 1.6 }}>
                  Thanks <strong>{form.name}</strong> — your message is on its way. We'll reply to{' '}
                  <strong>{form.email}</strong> within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="ia-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label htmlFor="ia-name" style={labelStyle}>Name</label>
                    <input id="ia-name" style={inputStyle} type="text" placeholder="Jane Doe" required
                      value={form.name} onChange={e => update('name', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="ia-email" style={labelStyle}>Work email</label>
                    <input id="ia-email" style={inputStyle} type="email" placeholder="jane@company.com" required
                      value={form.email} onChange={e => update('email', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="ia-company" style={labelStyle}>Company</label>
                    <input id="ia-company" style={inputStyle} type="text" placeholder="Company name" required
                      value={form.company} onChange={e => update('company', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="ia-team" style={labelStyle}>Team size</label>
                    <select id="ia-team" style={{ ...inputStyle, cursor: 'pointer' }}
                      value={form.teamSize} onChange={e => update('teamSize', e.target.value)}>
                      <option>1–5 people</option>
                      <option>6–20 people</option>
                      <option>21–50 people</option>
                      <option>50+ people</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <label htmlFor="ia-msg" style={labelStyle}>Query description</label>
                  <textarea id="ia-msg" rows={4}
                    style={{ ...inputStyle, height: 'auto', padding: '12px 14px', resize: 'vertical', lineHeight: 1.5 }}
                    placeholder="e.g. Set me up with the free ads.txt validator / quote for the full suite for 12 AdOps folks…"
                    value={form.message} onChange={e => update('message', e.target.value)} />
                </div>

                {/* honeypot */}
                <input type="text" name="botcheck" value={botcheck} onChange={e => setBotcheck(e.target.value)}
                  tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: 'none' }} />

                {error && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                    borderRadius: '10px', padding: '10px 12px', fontSize: '13px', marginTop: '14px',
                  }}>{error}</div>
                )}

                <button type="submit" disabled={loading} style={{
                  width: '100%', height: '50px', marginTop: '20px',
                  background: FLAME, color: INK, fontSize: '15px', fontWeight: 600,
                  border: 'none', borderRadius: '28px',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  {loading ? 'Sending…' : 'Send message'}
                </button>

                <p style={{ fontFamily: MONO, fontSize: '12px', color: '#a8a297', textAlign: 'center', marginTop: '12px' }}>
                  We'll never share your details. One reply, no drip campaigns.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

    </SiteLayout>
  );
}
